import { Container, Row, Col } from "react-bootstrap";
import { CheckCircle, ArrowRight } from 'react-bootstrap-icons';
import { trackEvent, trackServiceInquiry } from '../lib/analytics';
import { usePrefersReducedMotion } from '../lib/usePrefersReducedMotion';
import TrackVisibility from 'react-on-screen';
import 'animate.css';

const CALENDLY_URL = 'https://calendly.com/george-hoffmann-whiskeyrebel/discovery-call-30-minutes';

const pricingTiers = [
  {
    name: "Rebel Starter",
    formValue: "Starter",
    tagline: "Test the waters. Build the foundation.",
    blurb: "For the brand-new business that needs a quality web presence without burning the launch budget.",
    setup: "Starting at $1,800",
    monthly: "Starting at $350/mo",
    delivery: "21 business days or less",
    features: [
      "Single-page scrolling site (Hero, Services, About, Contact)",
      "Mobile-optimized + click-to-call + contact form",
      "Google Business Profile setup + 5 essential local citations",
      "Foundational SEO with Yoast",
      "Google Analytics + Search Console",
      "Monthly: GBP maintenance, 2 posts/mo, review monitoring, performance report"
    ]
  },
  {
    name: "Rebel Basic",
    formValue: "Basic",
    tagline: "Digital presence. Real credibility.",
    blurb: "For the established business ready to go deeper than a brochure.",
    setup: "Starting at $3,500",
    monthly: "Starting at $550/mo",
    delivery: "30 business days",
    features: [
      "5-page WordPress build with custom branding",
      "GBP optimization + 10 local citations",
      "On-page SEO with Yoast + SSL",
      "Google Analytics + Search Console",
      "Monthly: GBP maintenance, posts, review monitoring, monthly report"
    ]
  },
  {
    name: "Rebel Intermediate",
    formValue: "Intermediate",
    tagline: "Dominate locally. Own your market.",
    blurb: "For the business looking to dominate its local market.",
    setup: "Starting at $6,000",
    monthly: "Starting at $900/mo",
    delivery: "45 business days",
    features: [
      "8-page custom WordPress build",
      "Service area pages, advanced contact forms, testimonials",
      "20 local citations + advanced SEO + schema markup",
      "Speed + security hardening + GA4 conversion tracking",
      "Monthly: 4 GBP posts/mo, competitor analysis, review generation, quarterly reviews"
    ]
  },
  {
    name: "Rebel Pro",
    formValue: "Pro",
    tagline: "Scale your business. Go all in.",
    blurb: "For larger operations with multi-region reach or specialized custom system needs.",
    setup: "Starting at $15,000",
    monthly: "From $1,500/mo",
    delivery: "Final scope priced after kickoff call",
    features: [
      "12-page premium custom WordPress build + blog",
      "Booking, chat, and integration functionality",
      "CRM integration (GoHighLevel)",
      "50 local citations + content strategy + advanced SEO",
      "Custom dashboards + ROI reporting",
      "Monthly: full management — SEO, content, weekly GBP posts, strategy calls, priority support",
      "AI-Powered Digital Tools strongly recommended at this tier"
    ]
  }
];

export const Pricing = () => {
  const prefersReducedMotion = usePrefersReducedMotion();

  const handleGetStarted = (tier) => {
    trackEvent('pricing_get_started', {
      category: 'pricing',
      label: tier.formValue.toLowerCase()
    });
    sessionStorage.setItem('selectedTier', tier.formValue);
    const contactSection = document.getElementById('connect');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleStrategyClick = () => {
    trackServiceInquiry('pricing_strategy_session');
  };

  return (
    <section className="pricing pricing-v2" id="pricing" aria-labelledby="pricing-heading">
      <Container>
        <Row>
          <Col>
            <TrackVisibility once>
              {({ isVisible }) => {
                if (isVisible) {
                  trackEvent('pricing_section_view', {
                    category: 'pricing',
                    label: 'section_visible'
                  });
                }
                return (
                  <div className={`pricing-intro ${prefersReducedMotion ? "" : (isVisible ? "animate__animated animate__fadeIn" : "pre-reveal")}`}>
                    <h2 id="pricing-heading">The Rebel Design System</h2>
                    <p className="pricing-subhead">Four tiers. No mystery pricing. Built to grow with you.</p>
                  </div>
                );
              }}
            </TrackVisibility>

            <Row className="pricing-cards">
              {pricingTiers.map((tier, index) => (
                <Col key={tier.name} xs={12} sm={6} lg={3} className="pricing-col">
                  <TrackVisibility once>
                    {({ isVisible }) => (
                      <div className={`pricing-card ${prefersReducedMotion ? "" : (isVisible ? "animate__animated animate__fadeInUp" : "pre-reveal")}`} style={{ animationDelay: `${index * 80}ms` }}>
                        <div className="pricing-header">
                          <h3>{tier.name}</h3>
                          <p className="tagline">{tier.tagline}</p>
                          <p className="blurb">{tier.blurb}</p>
                        </div>

                        <div className="pricing-cost">
                          <div className="setup-cost">
                            <span className="amount">{tier.setup}</span>
                            <span className="period">setup</span>
                          </div>
                          <div className="monthly-cost">
                            <span className="amount">{tier.monthly}</span>
                          </div>
                          <div className="delivery">
                            <span>Delivery: {tier.delivery}</span>
                          </div>
                        </div>

                        <ul className="features-list">
                          {tier.features.map((feature, idx) => (
                            <li key={idx}>
                              <CheckCircle className="check-icon" aria-hidden="true" />
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>

                        <div className="pricing-actions">
                          <button
                            className="btn-get-started"
                            onClick={() => handleGetStarted(tier)}
                            aria-label={`Get started with ${tier.name}`}
                          >
                            Get Started <ArrowRight aria-hidden="true" />
                          </button>
                        </div>
                      </div>
                    )}
                  </TrackVisibility>
                </Col>
              ))}
            </Row>

            <TrackVisibility once>
              {({ isVisible }) => (
                <a
                  href={CALENDLY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleStrategyClick}
                  className={`pricing-custom-card ${prefersReducedMotion ? "" : (isVisible ? "animate__animated animate__fadeInUp" : "pre-reveal")}`}
                  aria-label="Rebel Custom tier — book a strategy session"
                >
                  <div className="pricing-custom-card-meta">
                    <span className="pricing-custom-eyebrow">Rebel Custom</span>
                    <h3 className="pricing-custom-headline">Multi-region, complex SaaS, or full-stack rebuilds.</h3>
                    <p className="pricing-custom-sub">For owners whose project doesn't fit a card. Priced after a scoping conversation.</p>
                  </div>
                  <span className="pricing-custom-cta">
                    Let's talk <ArrowRight aria-hidden="true" />
                    <span className="visually-hidden"> (opens Calendly in a new tab)</span>
                  </span>
                </a>
              )}
            </TrackVisibility>

            <Row className="pricing-callouts">
              <Col xs={12}>
                <p className="callout callout-equine">
                  Running a stable, racing syndicate, or equestrian operation? We have a <a href="#equine">purpose-built equine track</a> starting at $5,500.
                </p>
                <p className="callout callout-addons">
                  Add Google Ads or Meta Ads management starting at $500/mo. Add AI-powered tools — receptionist, reputation management, conversational AI — starting at $299/mo.
                </p>
              </Col>
            </Row>

            <Row className="pricing-final-cta">
              <Col xs={12}>
                <p>Not sure which tier fits? Book a free strategy session — I'll tell you straight.</p>
                <a
                  href={CALENDLY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-strategy"
                  onClick={handleStrategyClick}
                >
                  Book a Free Strategy Session <ArrowRight aria-hidden="true" />
                  <span className="visually-hidden"> (opens in new tab)</span>
                </a>
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>
    </section>
  );
};
